import type { MeResponse } from './api'

/**
 * Home's `<h1>`: a time-of-day greeting, plus ", <name>" when the account
 * carries one (Sprint 1.9.9 P5, R53).
 *
 * ── THE PART OF DAY ─────────────────────────────────────────────────────────
 *
 * "Good morning" from 05:00, "Good afternoon" from 12:00, "Good evening" from
 * 18:00 until 05:00 — on the browser's LOCAL clock, because a greeting is about
 * the reader's day and not the server's. The three bounds are the only numbers
 * here, and they are an hour of the day compared with an hour of the day: time,
 * never a measurement, which is the NOT_SCIENCE row `clientScience.test.ts`
 * carries for this file (P7).
 *
 * ── WHERE THE NAME COMES FROM, AND WHERE IT NEVER COMES FROM ────────────────
 *
 * `/me` carries email, role, status and timestamps, and no name
 * (`MeResponse`, `extra="forbid"` on the backend). Sprint 1.9.6 D2 asks for a
 * greeting that works today and takes a name the day one exists (1.9.9 O4), so
 * the field is read STRUCTURALLY — the shape `trend.ts` uses for
 * `scaleBaseline` — and the name it is read under, `display_name`, is the one
 * filed with the backend as ask §28 in `docs/backend-follow-ups.md`. It compiles
 * whether or not that field ever reaches the generated types.
 *
 * It is never derived from the email. `initials.ts` sets the rule — "Nothing is
 * invented" — and a local part is not a name: `visferraz@gmail.com` would be
 * greeted as "visferraz". Nor from any other field that happens to look like a
 * name: a greeting built from a guess is a claim about who someone is.
 *
 * ── WHY IT IS THE SAME IN EVERY STATE ───────────────────────────────────────
 *
 * The account is loaded before any product route mounts (`RequireAuth`), and
 * the caller passes the instant it captured ONCE rather than asking the clock
 * again (Home's `today`). So loading, first run, established and every failure
 * render the same heading, and no fetch changes it — R36's invariant, which
 * R53 keeps while replacing its "Hello". The landing assertions read it, and a
 * heading that changes after a fetch is what `settle.ts` warns about.
 */
export function greetingFor(account: MeResponse | null, now: Date): string {
  const raw: unknown = (account as (MeResponse & { display_name?: unknown }) | null)?.display_name
  const name = typeof raw === 'string' ? raw.trim() : ''
  const part = partOfDay(now)
  return name === '' ? part : `${part}, ${name}`
}

export type PartOfDay = 'Good morning' | 'Good afternoon' | 'Good evening'

/** The greeting's words for the local hour of `now`. */
export function partOfDay(now: Date): PartOfDay {
  const hour = now.getHours()
  if (hour < 5 || hour >= 18) return 'Good evening'
  if (hour < 12) return 'Good morning'
  return 'Good afternoon'
}
